import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import LoadingSpinner from '../components/LoadingSpinner';
import { fetcher, parseApiError } from '../lib/api';
import { getBackendUrl } from '../lib/backend';
import { getCustomerDisplayLabel, formatCustomerPhone } from '../lib/customer';
import { InteractionTypeIcon } from '../components/InteractionTypeIcon';

interface Customer {
  id: string;
  phone: string;
  name: string | null;
  email: string | null;
  routingState: 'BOT' | 'HUMAN';
  lastActivityAt: string;
  handedOffAt: string | null;
}

interface Interaction {
  id: string;
  type: 'voice' | 'chat' | 'whatsapp' | 'phone';
  startTime: string;
  endTime: string | null;
  messageCount: number | null;
  audioSeconds: number | null;
}

interface ConversationTurn {
  role: 'user' | 'model';
  content: string;
  timestamp: string;
}

interface FullInteraction extends Interaction {
  conversationData?: {
    turns: ConversationTurn[];
  };
}

function formatDate(dateString: string) {
  return format(new Date(dateString), 'dd-MMM-yy, HH:mm', { locale: es });
}

const CUSTOMER_PAGE_SIZE = 50;

export function Customers() {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedInteractionId, setSelectedInteractionId] = useState<string | null>(null);
  const [routingLoading, setRoutingLoading] = useState(false);
  const [routingError, setRoutingError] = useState<string | null>(null);

  const customersUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (query.trim()) {
      params.set('q', query.trim());
    }
    params.set('offset', String(offset));
    return `${getBackendUrl()}/api/customers?${params.toString()}`;
  }, [query, offset]);

  const {
    data: customersPage,
    error: customersError,
    isLoading: customersLoading,
    mutate: mutateCustomers,
  } = useSWR<{ customers: Customer[]; hasMore: boolean }>(customersUrl, fetcher);

  const customersData = useMemo(() => ({ customers: allCustomers }), [allCustomers]);

  useEffect(() => {
    setOffset(0);
    setAllCustomers([]);
    setHasMore(false);
  }, [query]);

  useEffect(() => {
    if (!customersPage) return;

    setHasMore(customersPage.hasMore);
    setAllCustomers((current) => {
      if (offset === 0) {
        return customersPage.customers;
      }

      const existingIds = new Set(current.map((customer) => customer.id));
      const nextCustomers = customersPage.customers.filter(
        (customer) => !existingIds.has(customer.id),
      );
      return [...current, ...nextCustomers];
    });
  }, [customersPage, offset]);

  const selectedCustomer = customersData?.customers.find(
    (customer) => customer.id === selectedCustomerId,
  );

  const { data: interactionsData, isLoading: interactionsLoading } = useSWR<{ interactions: Interaction[] }>(
    selectedCustomerId ? `${getBackendUrl()}/api/customers/${selectedCustomerId}/interactions` : null,
    fetcher,
  );

  const { data: selectedInteractionData, isLoading: isLoadingInteraction } = useSWR<FullInteraction>(
    selectedCustomerId && selectedInteractionId
      ? `${getBackendUrl()}/api/customers/${selectedCustomerId}/interactions/${selectedInteractionId}`
      : null,
    fetcher,
  );

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setQuery(search);
    setSelectedCustomerId(null);
    setSelectedInteractionId(null);
  };

  const handleLoadMore = () => {
    setOffset((current) => current + CUSTOMER_PAGE_SIZE);
  };

  const handleReturnToBot = async () => {
    if (!selectedCustomer) return;

    setRoutingLoading(true);
    setRoutingError(null);

    try {
      const response = await fetch(
        `${getBackendUrl()}/api/customers/${selectedCustomer.id}/routing`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ state: 'BOT' }),
        },
      );

      if (!response.ok) {
        throw await parseApiError(response, 'No se pudo devolver la conversación al bot');
      }

      setOffset(0);
      await mutateCustomers();
    } catch (error) {
      setRoutingError(error instanceof Error ? error.message : 'Error al actualizar enrutamiento');
    } finally {
      setRoutingLoading(false);
    }
  };

  if (customersError) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-red-500">No se pudieron cargar los clientes</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="mb-2 text-3xl font-bold text-primary">Clientes</h1>
      <p className="mb-6 text-gray-600">
        Revisa prospectos y conversaciones de WhatsApp. Cuando un cliente está en modo humano,
        responde desde la app de WhatsApp en tu teléfono.
      </p>

      <div className="mb-6 max-w-xl">
        <form onSubmit={handleSearchSubmit} className="flex gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por teléfono, nombre o correo"
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <button
            type="submit"
            className="rounded-md bg-primary px-4 py-2 font-medium text-white hover:bg-primary/90"
          >
            Buscar
          </button>
        </form>
      </div>

      <div className="grid min-h-[70vh] grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md">
            <div className="border-b bg-gray-50 px-4 py-3 font-medium text-gray-700">
              Clientes ({customersData?.customers.length ?? 0})
            </div>
            {customersLoading ? (
              <div className="flex h-64 items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : customersData?.customers.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No hay clientes todavía.</div>
            ) : (
              <ul className="max-h-[65vh] divide-y divide-gray-200 overflow-y-auto">
                {customersData?.customers.map((customer) => {
                  const isSelected = customer.id === selectedCustomerId;
                  const label = getCustomerDisplayLabel(customer.name, customer.phone);

                  return (
                    <li key={customer.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCustomerId(customer.id);
                          setSelectedInteractionId(null);
                          setRoutingError(null);
                        }}
                        className={`w-full px-4 py-3 text-left transition-colors ${
                          isSelected ? 'bg-primary/10' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-gray-900">{label}</span>
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                              customer.routingState === 'HUMAN'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-green-100 text-green-700'
                            }`}
                          >
                            {customer.routingState === 'HUMAN' ? 'Humano' : 'Bot'}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">{formatCustomerPhone(customer.phone)}</p>
                        {customer.email && (
                          <p className="text-sm text-gray-500">{customer.email}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-400">
                          Actividad: {formatDate(customer.lastActivityAt)}
                        </p>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {hasMore && !customersLoading && (
              <div className="border-t px-4 py-3">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  className="w-full rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cargar más clientes
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-8">
          {!selectedCustomer ? (
            <div className="flex h-full min-h-[20rem] items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white p-8 text-gray-500">
              Selecciona un cliente para ver sus conversaciones.
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-white shadow-md">
              <div className="flex flex-wrap items-start justify-between gap-4 border-b px-6 py-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {getCustomerDisplayLabel(selectedCustomer.name, selectedCustomer.phone)}
                  </h2>
                  <p className="text-sm text-gray-500">{formatCustomerPhone(selectedCustomer.phone)}</p>
                  {selectedCustomer.email && (
                    <p className="text-sm text-gray-500">{selectedCustomer.email}</p>
                  )}
                </div>
                {selectedCustomer.routingState === 'HUMAN' && (
                  <button
                    type="button"
                    onClick={handleReturnToBot}
                    disabled={routingLoading}
                    className="rounded-md bg-secondary px-4 py-2 text-sm font-medium text-white hover:bg-secondary/90 disabled:opacity-50"
                  >
                    {routingLoading ? 'Guardando...' : 'Devolver al bot'}
                  </button>
                )}
              </div>

              {routingError && (
                <div className="border-b bg-red-50 px-6 py-3 text-sm text-red-700">{routingError}</div>
              )}

              <div className="grid grid-cols-1 gap-0 lg:grid-cols-2">
                <div className="border-b lg:border-b-0 lg:border-r">
                  <div className="border-b bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">
                    Conversaciones
                  </div>
                  {interactionsLoading ? (
                    <div className="flex h-48 items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  ) : interactionsData?.interactions.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      Este cliente aún no tiene conversaciones guardadas.
                    </div>
                  ) : (
                    <ul className="max-h-[50vh] divide-y divide-gray-200 overflow-y-auto">
                      {interactionsData?.interactions.map((interaction) => (
                        <li key={interaction.id}>
                          <button
                            type="button"
                            onClick={() => setSelectedInteractionId(interaction.id)}
                            className={`w-full px-4 py-3 text-left hover:bg-gray-50 ${
                              selectedInteractionId === interaction.id ? 'bg-primary/5' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <InteractionTypeIcon type={interaction.type} />
                              <span className="text-sm font-medium text-gray-800">
                                {formatDate(interaction.startTime)}
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              {interaction.messageCount ?? 0} mensajes
                            </p>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <div className="border-b bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">
                    Transcripción
                  </div>
                  {!selectedInteractionId ? (
                    <div className="flex h-48 items-center justify-center p-6 text-center text-gray-500">
                      Selecciona una conversación para ver la transcripción.
                    </div>
                  ) : isLoadingInteraction ? (
                    <div className="flex h-48 items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="max-h-[50vh] space-y-4 overflow-y-auto p-4">
                      {selectedInteractionData?.conversationData?.turns?.length ? (
                        selectedInteractionData.conversationData.turns.map((turn, index) => (
                          <div
                            key={index}
                            className={`flex ${turn.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-md rounded-lg px-4 py-2 shadow ${
                                turn.role === 'user'
                                  ? 'bg-primary/10 text-primary/80'
                                  : 'bg-secondary/10 text-secondary/80'
                              }`}
                            >
                              <p className="whitespace-pre-wrap text-sm">{turn.content}</p>
                              {turn.timestamp && (
                                <p className="mt-1 text-right text-xs text-gray-500">
                                  {formatDate(turn.timestamp)}
                                </p>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-center text-gray-500">
                          No hay transcripción disponible para esta conversación.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Customers;
